import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseKey);

// Guard client creation to prevent crashes before the fallback screen can show
export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
      realtime: {
        params: { eventsPerSecond: 10 },
      },
    })
  : (new Proxy({} as ReturnType<typeof createClient>, {
      get(_target, prop) {
        if (prop === 'from' || prop === 'rpc' || prop === 'auth' || prop === 'channel' || prop === 'getChannels' || prop === 'removeChannel') {
          return () => {
            throw new Error('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
          };
        }
        return undefined;
      },
    }) as ReturnType<typeof createClient>);

if (import.meta.env.DEV && isSupabaseConfigured) {
  console.log('[Supabase] Client initialized');
}

// ─── Auth helpers ────────────────────────────────────────────────────
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const getCurrentSession = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
};

// Connectivity test
export const pingSupabase = async () => {
  try {
    const { data, error } = await supabase.from('products').select('id').limit(1);
    if (error) throw error;
    return { success: true, data };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    return { success: false, error: message };
  }
};

// ─── Database Types ──────────────────────────────────────────────────
export interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  address: Array<{
    id: string;
    label: string;
    street: string;
    area?: string;
    city: string;
    postal_code?: string;
    is_default: boolean;
    type: 'home' | 'work' | 'other';
  }> | null;
  role: 'customer' | 'admin' | 'manager' | 'staff';
  avatar_url: string | null;
  preferred_language: 'bn' | 'en';
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  name_bn: string;
  name_en: string;
  description_bn: string | null;
  description_en: string | null;
  price: number;
  compare_price: number | null;
  weight_grams: number;
  category: string;
  ingredients: string[];
  shelf_life_days: number;
  storage_temp: string;
  images: string[];
  stock_quantity: number;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

export interface Order {
  id: string;
  user_id: string | null;
  guest_email?: string | null;
  guest_name?: string | null;
  guest_phone?: string | null;
  status: 'pending' | 'confirmed' | 'packaging' | 'shipped' | 'delivered' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  payment_method: 'cod' | 'bkash' | 'nagad' | 'card' | 'bank_transfer' | null;
  subtotal: number;
  delivery_fee: number;
  discount: number;
  voucher_id: string | null;
  total: number;
  delivery_address: {
    street: string;
    area?: string;
    city: string;
    thana?: string;
    postal_code?: string;
  };
  contact_phone: string;
  notes: string | null;
  assigned_staff_id: string | null;
  assigned_at: string | null;
  created_at: string;
  updated_at: string;
  order_items?: OrderItem[];
  profiles?: { full_name: string | null; email: string; phone: string | null };
  staff?: { full_name: string | null };
  // Courier fields
  courier_provider?: string | null;
  courier_consignment_id?: string | null;
  courier_tracking_url?: string | null;
  courier_status?: string | null;
  courier_shipped_at?: string | null;
  // Payment fields
  payment_transaction_id?: string | null;
  payment_gateway?: string | null;
  payment_verified_at?: string | null;
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string | null;
  quantity: number;
  price_at_time: number;
  total: number;
  created_at: string;
  products?: Partial<Product>;
}

export interface Voucher {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_order_amount: number;
  max_discount_amount: number | null;
  start_date: string;
  end_date: string | null;
  usage_limit: number | null;
  usage_count: number;
  is_active: boolean;
  created_at: string;
}

export interface Carousel {
  id: string;
  title: string | null;
  subtitle: string | null;
  button_text: string | null;
  image_url: string;
  bg_gradient: string | null;
  title_color: string;
  subtitle_color: string;
  button_bg: string;
  button_hover_bg: string;
  is_active: boolean;
  display_order: number;
  redirect_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentConfig {
  id: string;
  gateway: 'bkash' | 'nagad' | 'cod';
  is_active: boolean;
  app_key: string | null;
  app_secret: string | null;
  merchant_id: string | null;
  merchant_username: string | null;
  merchant_password: string | null;
  api_base_url: string;
  api_callback_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface CourierConfig {
  id: string;
  provider: 'pathao' | 'steadfast';
  is_active: boolean;
  api_base_url: string;
  api_key: string | null;
  api_secret: string | null;
  store_id: string | null;
  merchant_code: string | null;
  default_city_id: string | null;
  default_zone_id: string | null;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeliveryShipment {
  id: string;
  order_id: string;
  courier_provider: string;
  consignment_id: string;
  tracking_code: string | null;
  tracking_url: string | null;
  status: string;
  recipient_name: string;
  recipient_phone: string;
  recipient_address: string;
  recipient_city: string;
  recipient_zone: string | null;
  recipient_area: string | null;
  parcel_weight: number;
  parcel_type: string;
  delivery_type: string;
  cod_amount: number;
  special_instruction: string | null;
  api_response: Record<string, unknown>;
  requested_at: string;
  picked_up_at: string | null;
  delivered_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentTransaction {
  id: string;
  order_id: string;
  gateway: string;
  payment_id: string | null;
  transaction_id: string | null;
  trx_id: string | null;
  merchant_invoice_number: string | null;
  amount: number;
  currency: string;
  status: string;
  gateway_response: Record<string, unknown>;
  refund_amount: number;
  refund_reason: string | null;
  refunded_at: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  updated_at: string;
}
